/**
 * R-9 — cross-section coherence audit (Gemini Flash judge, user-audit-only).
 */
import { Type } from '@google/genai';
import { AUDIT_CONFIG } from '../../config/auditConfig';
import type { ReportTypeId } from '../../config/reportTypes';
import type { SiteRagReport } from '../../types/siteRag';
import type { AuditSeverity } from '../../types/userAudit';
import { GEMINI_PRO_MODEL } from '../../config/geminiModels';
import { ai } from '../../services/geminiClient';
import { tracedGeminiCall } from '../telemetry/tracedGeminiCall';
import { logAuditEvent } from '../telemetry/auditTelemetry';
import { extractSections } from './reportTextExtract';
import registryJson from './data/sectionPairRegistry.json';
import {
  buildSectionCoherenceHarmonizePrompt,
  buildSectionCoherenceJudgePrompt,
} from './prompts/sectionCoherenceJudge';

const JUDGE_MODEL = GEMINI_PRO_MODEL;

export interface SectionPair {
  pairId: string;
  sectionA: string;
  sectionB: string;
  topic: string;
  priority: number;
  contentA: string;
  contentB: string;
}

export interface CoherenceJudgement {
  coherent: boolean;
  contradictionSeverity: 'none' | 'minor' | 'major' | 'critical';
  topic: string;
  sectionAClaim?: string;
  sectionBClaim?: string;
  rationale: string;
  parseError?: boolean;
}

export interface CoherenceFinding {
  pairId: string;
  sectionA: string;
  sectionB: string;
  topic: string;
  severity: AuditSeverity;
  message: string;
  rationale: string;
  sectionAClaim?: string;
  sectionBClaim?: string;
  suggestedHarmonization?: string;
}

export interface SectionCoherenceAuditMeta {
  pairsEvaluated: number;
  pairsSelected: number;
  findingsCount: number;
  autocorrectAttempted: boolean;
  durationMs: number;
}

export type CoherenceJudgeFn = (
  sectionA: { id: string; content: string },
  sectionB: { id: string; content: string },
  topic: string,
  reportType: string,
) => Promise<CoherenceJudgement | null>;

interface PairRule {
  reportTypes: string[];
  sectionA: string;
  sectionB: string;
  topic: string;
  priority: number;
}

interface SectionPairRegistry {
  version: number;
  defaultMaxPairs: number;
  maxPairsCap: number;
  minSectionChars: number;
  pairRules: PairRule[];
}

const REGISTRY = registryJson as SectionPairRegistry;

function pairKey(a: string, b: string): string {
  return [a, b].sort().join('::');
}

function ruleMatchesReportType(rule: PairRule, reportType: string): boolean {
  return rule.reportTypes.includes('*') || rule.reportTypes.includes(reportType);
}

/** Deterministic pair selection — registry rules + both sections present with min content. */
export function selectSectionPairsForCoherence(
  report: SiteRagReport,
  opts: { maxPairs?: number } = {},
): SectionPair[] {
  const reportType = (report.reportType ?? 'feasibility') as ReportTypeId;
  const sections = extractSections(report);
  const byId = new Map(sections.map((s) => [s.id, s.content.trim()]));
  const minChars = REGISTRY.minSectionChars;

  const cap = Math.min(
    opts.maxPairs ?? REGISTRY.defaultMaxPairs,
    AUDIT_CONFIG.SECTION_COHERENCE_MAX_PAIRS,
    REGISTRY.maxPairsCap,
  );

  const candidates: SectionPair[] = [];
  const seen = new Set<string>();

  for (const rule of REGISTRY.pairRules) {
    if (!ruleMatchesReportType(rule, reportType)) continue;

    const contentA = byId.get(rule.sectionA) ?? '';
    const contentB = byId.get(rule.sectionB) ?? '';
    if (contentA.length < minChars || contentB.length < minChars) continue;

    const key = pairKey(rule.sectionA, rule.sectionB);
    if (seen.has(key)) continue;
    seen.add(key);

    candidates.push({
      pairId: key,
      sectionA: rule.sectionA,
      sectionB: rule.sectionB,
      topic: rule.topic,
      priority: rule.priority,
      contentA,
      contentB,
    });
  }

  candidates.sort((a, b) => {
    if (b.priority !== a.priority) return b.priority - a.priority;
    return a.pairId.localeCompare(b.pairId);
  });

  return candidates.slice(0, cap);
}

function parseJudgeResponse(text: string | undefined | null, topic: string): CoherenceJudgement | null {
  if (!text?.trim()) return null;
  try {
    const parsed = JSON.parse(text) as {
      coherent?: boolean;
      contradictionSeverity?: string;
      topic?: string;
      sectionAClaim?: string;
      sectionBClaim?: string;
      rationale?: string;
    };
    const sev = parsed.contradictionSeverity;
    const contradictionSeverity: CoherenceJudgement['contradictionSeverity'] =
      sev === 'critical' || sev === 'major' || sev === 'minor' || sev === 'none'
        ? sev
        : 'none';
    const coherent = typeof parsed.coherent === 'boolean'
      ? parsed.coherent
      : contradictionSeverity === 'none';
    return {
      coherent,
      contradictionSeverity,
      topic: typeof parsed.topic === 'string' ? parsed.topic : topic,
      sectionAClaim: typeof parsed.sectionAClaim === 'string' ? parsed.sectionAClaim : undefined,
      sectionBClaim: typeof parsed.sectionBClaim === 'string' ? parsed.sectionBClaim : undefined,
      rationale: typeof parsed.rationale === 'string' ? parsed.rationale : '',
    };
  } catch {
    return {
      coherent: true,
      contradictionSeverity: 'none',
      topic,
      rationale: 'Judge response malformed — skipped.',
      parseError: true,
    };
  }
}

function mapJudgementToSeverity(j: CoherenceJudgement): AuditSeverity | null {
  if (j.parseError || j.coherent || j.contradictionSeverity === 'none') return null;
  if (j.contradictionSeverity === 'critical') return 'block';
  if (j.contradictionSeverity === 'major') return 'warn';
  return 'info';
}

function buildFinding(
  pair: SectionPair,
  judgement: CoherenceJudgement,
  severity: AuditSeverity,
): CoherenceFinding {
  return {
    pairId: pair.pairId,
    sectionA: pair.sectionA,
    sectionB: pair.sectionB,
    topic: judgement.topic || pair.topic,
    severity,
    message:
      `Sections "${pair.sectionA}" and "${pair.sectionB}" contradict on ${judgement.topic || pair.topic}`,
    rationale: judgement.rationale,
    sectionAClaim: judgement.sectionAClaim,
    sectionBClaim: judgement.sectionBClaim,
  };
}

/** Default Gemini Flash judge — overridable in tests. */
export const defaultCoherenceJudgeFn: CoherenceJudgeFn = async (sectionA, sectionB, topic, reportType) => {
  const prompt = buildSectionCoherenceJudgePrompt({
    reportType,
    topic,
    sectionAId: sectionA.id,
    sectionAContent: sectionA.content,
    sectionBId: sectionB.id,
    sectionBContent: sectionB.content,
  });

  try {
    const response = await tracedGeminiCall(
      JUDGE_MODEL,
      () => ai.models.generateContent({
        model: JUDGE_MODEL,
        contents: prompt,
        config: {
          responseMimeType: 'application/json',
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              coherent: { type: Type.BOOLEAN },
              contradictionSeverity: {
                type: Type.STRING,
                description: 'One of: none, minor, major, critical',
              },
              topic: { type: Type.STRING },
              sectionAClaim: { type: Type.STRING },
              sectionBClaim: { type: Type.STRING },
              rationale: { type: Type.STRING },
            },
            required: ['coherent', 'contradictionSeverity', 'rationale'],
          },
        },
      }),
      { stage: 'section-coherence-judge', reportType },
    );
    return parseJudgeResponse(response.text, topic);
  } catch (err) {
    console.warn('[sectionCoherenceAuditor] judge failed:', (err as Error)?.message);
    return null;
  }
};

async function suggestHarmonization(
  pair: SectionPair,
  finding: CoherenceFinding,
  reportType: string,
): Promise<string | undefined> {
  const prompt = buildSectionCoherenceHarmonizePrompt({
    reportType,
    topic: finding.topic,
    sectionAId: pair.sectionA,
    sectionAContent: pair.contentA,
    sectionBId: pair.sectionB,
    sectionBContent: pair.contentB,
    rationale: finding.rationale,
  });

  try {
    const response = await tracedGeminiCall(
      JUDGE_MODEL,
      () => ai.models.generateContent({
        model: JUDGE_MODEL,
        contents: prompt,
        config: {
          responseMimeType: 'application/json',
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              suggestedSentence: { type: Type.STRING },
              targetSectionId: { type: Type.STRING },
            },
            required: ['suggestedSentence'],
          },
        },
      }),
      { stage: 'section-coherence-harmonize', reportType },
    );
    const parsed = JSON.parse(response.text ?? '{}') as { suggestedSentence?: string };
    return typeof parsed.suggestedSentence === 'string' ? parsed.suggestedSentence : undefined;
  } catch {
    return undefined;
  }
}

export async function judgeCoherence(
  sectionA: { id: string; content: string },
  sectionB: { id: string; content: string },
  llm: CoherenceJudgeFn,
  topic: string,
  reportType: string,
): Promise<CoherenceJudgement> {
  const verdict = await llm(sectionA, sectionB, topic, reportType);
  if (!verdict) {
    return {
      coherent: true,
      contradictionSeverity: 'none',
      topic,
      rationale: 'Judge unavailable — skipped.',
      parseError: true,
    };
  }
  return verdict;
}

export interface AuditSectionCoherenceOptions {
  maxPairs?: number;
  judgeFn?: CoherenceJudgeFn;
  reportId?: string;
  siteId?: string;
  sessionId?: string;
  autocorrect?: boolean;
}

/** Run coherence audit for all selected pairs on one report. */
export async function auditSectionCoherence(
  report: SiteRagReport,
  opts: AuditSectionCoherenceOptions = {},
): Promise<{ findings: CoherenceFinding[]; meta: SectionCoherenceAuditMeta }> {
  const started = Date.now();
  const reportType = report.reportType ?? 'feasibility';
  const pairs = selectSectionPairsForCoherence(report, { maxPairs: opts.maxPairs });
  const judgeFn = opts.judgeFn ?? defaultCoherenceJudgeFn;
  const autocorrect = opts.autocorrect ?? AUDIT_CONFIG.AUTOCORRECT_COHERENCE;

  const findings: CoherenceFinding[] = [];
  let autocorrectAttempted = false;

  for (const pair of pairs) {
    const judgement = await judgeCoherence(
      { id: pair.sectionA, content: pair.contentA },
      { id: pair.sectionB, content: pair.contentB },
      judgeFn,
      pair.topic,
      reportType,
    );

    const severity = mapJudgementToSeverity(judgement);
    if (!severity) continue;

    const finding = buildFinding(pair, judgement, severity);

    if (autocorrect && !autocorrectAttempted && severity === 'warn') {
      autocorrectAttempted = true;
      const suggestion = await suggestHarmonization(pair, finding, reportType);
      if (suggestion) finding.suggestedHarmonization = suggestion;
    }

    findings.push(finding);
  }

  const meta: SectionCoherenceAuditMeta = {
    pairsEvaluated: pairs.length,
    pairsSelected: pairs.length,
    findingsCount: findings.length,
    autocorrectAttempted,
    durationMs: Date.now() - started,
  };

  logAuditEvent({
    type: 'section-coherence-audit-completed',
    sessionId: opts.sessionId,
    siteId: opts.siteId,
    reportId: opts.reportId ?? report.id,
    reportType,
    payload: {
      pairsEvaluated: meta.pairsEvaluated,
      findingsCount: meta.findingsCount,
      block: findings.filter((f) => f.severity === 'block').length,
      warn: findings.filter((f) => f.severity === 'warn').length,
      durationMs: meta.durationMs,
    },
  });

  return { findings, meta };
}
